// Phase 3 — Composes shop pricing settings + category rules + matrix rows
// into a single ShopPricingConfig used by suggestedSellHybrid.
//
// AUTHORITY (FF-B / FF-E):
//   shop_pricing_settings + shop_pricing_category_rules +
//   shop_pricing_matrix_rows WRITES are gated server-side by
//   `trg_enforce_parts_pricing_entitlement_*` triggers
//   (migration `20260501130000_ff_b_parts_entitlement_triggers.sql`).
//   READS are additionally gated by FF-E's
//   `ff_e_shop_pricing_*_select_entitled` RESTRICTIVE policies
//   (migration `20260502120000_ff_e_financial_read_authority.sql`).
//   A sessionStorage `partsPricing` feature-flag override cannot bypass
//   either layer; non-entitled shops fall back to FALLBACK_PRICING_MATRIX
//   because their queries return empty.
import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  DEFAULT_MATRIX_KEY,
  emptyShopPricingConfig,
  type ShopPricingCategoryRule,
  type ShopPricingConfig,
  type PricingMatrixRow,
  type PricingMode,
  FALLBACK_PRICING_MATRIX,
} from '@/lib/parts-financial/calculator';

export interface ShopPricingSettingsRow {
  shop_id: string;
  default_mode: PricingMode;
  optiguard_enabled: boolean;
  global_max_multiplier: number;
  global_max_markup_pct: number;
  optiguard_max_step_pct: number;
  optiguard_min_sample_count: number;
  // Phase 3.5
  matrix_quantity_mode: 'per_unit' | 'grouped';
  min_gp_warning_pct: number;
  use_suggested_by_default: boolean;
  exclude_canned_from_learning: boolean;
}

export interface ShopPricingCategoryRuleRow {
  id: string;
  shop_id: string;
  category: string;
  pricing_mode: PricingMode | null;
  markup_percent: number | null;
  optiguard_target_gp: number | null;
  min_markup_pct: number | null;
  max_markup_pct: number | null;
  enabled: boolean;
}

export interface ShopPricingMatrixRowRow {
  id: string;
  shop_id: string;
  category: string | null;
  min_cost: number;
  max_cost: number | null;
  multiplier: number;
  sort_order: number;
}

const settingsKey = (shopId: string) => ['shop-pricing-settings', shopId] as const;
const rulesKey = (shopId: string) => ['shop-pricing-category-rules', shopId] as const;
const matrixKey = (shopId: string) => ['shop-pricing-matrix-rows', shopId] as const;

export function useShopPricingSettings(shopId: string | undefined) {
  return useQuery({
    queryKey: settingsKey(shopId ?? ''),
    enabled: !!shopId,
    queryFn: async (): Promise<ShopPricingSettingsRow | null> => {
      const { data, error } = await (supabase as any)
        .from('shop_pricing_settings')
        .select('*')
        .eq('shop_id', shopId)
        .maybeSingle();
      if (error) throw error;
      return data as ShopPricingSettingsRow | null;
    },
  });
}

export function useShopPricingCategoryRules(shopId: string | undefined) {
  return useQuery({
    queryKey: rulesKey(shopId ?? ''),
    enabled: !!shopId,
    queryFn: async (): Promise<ShopPricingCategoryRuleRow[]> => {
      const { data, error } = await (supabase as any)
        .from('shop_pricing_category_rules')
        .select('*')
        .eq('shop_id', shopId)
        .order('category', { ascending: true });
      if (error) throw error;
      return (data ?? []) as ShopPricingCategoryRuleRow[];
    },
  });
}

export function useShopPricingMatrixRows(shopId: string | undefined) {
  return useQuery({
    queryKey: matrixKey(shopId ?? ''),
    enabled: !!shopId,
    queryFn: async (): Promise<ShopPricingMatrixRowRow[]> => {
      const { data, error } = await (supabase as any)
        .from('shop_pricing_matrix_rows')
        .select('*')
        .eq('shop_id', shopId)
        .order('category', { ascending: true })
        .order('sort_order', { ascending: true });
      if (error) throw error;
      return (data ?? []) as ShopPricingMatrixRowRow[];
    },
  });
}

/** Composes the three queries into a ShopPricingConfig usable by the engine. */
export function useShopPricingConfig(shopId: string | undefined): {
  config: ShopPricingConfig;
  isLoading: boolean;
  hasShopConfig: boolean;
} {
  const sQ = useShopPricingSettings(shopId);
  const rQ = useShopPricingCategoryRules(shopId);
  const mQ = useShopPricingMatrixRows(shopId);

  const config = useMemo<ShopPricingConfig>(() => {
    const base = emptyShopPricingConfig();
    if (!sQ.data && !rQ.data?.length && !mQ.data?.length) return base;

    const settings = sQ.data;
    const categoryRules = new Map<string, ShopPricingCategoryRule>();
    for (const r of rQ.data ?? []) {
      if (!r.enabled) continue;
      categoryRules.set(r.category, {
        mode: r.pricing_mode ?? undefined,
        markupPct: r.markup_percent ?? undefined,
        targetGp: r.optiguard_target_gp ?? undefined,
        minMarkupPct: r.min_markup_pct ?? undefined,
        maxMarkupPct: r.max_markup_pct ?? undefined,
      });
    }

    const matrixByCategory = new Map<string, PricingMatrixRow[]>();
    const grouped = new Map<string, ShopPricingMatrixRowRow[]>();
    for (const row of mQ.data ?? []) {
      const key = row.category ?? DEFAULT_MATRIX_KEY;
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key)!.push(row);
    }
    for (const [key, rows] of grouped.entries()) {
      const sorted = [...rows].sort((a, b) => a.sort_order - b.sort_order || a.min_cost - b.min_cost);
      matrixByCategory.set(
        key,
        sorted.map((r) => ({
          maxCost: r.max_cost == null ? Number.POSITIVE_INFINITY : Number(r.max_cost),
          multiplier: Number(r.multiplier),
        })),
      );
    }
    if (!matrixByCategory.has(DEFAULT_MATRIX_KEY)) {
      matrixByCategory.set(DEFAULT_MATRIX_KEY, FALLBACK_PRICING_MATRIX);
    }

    return {
      defaultMode: (settings?.default_mode ?? 'matrix') as PricingMode,
      globalMaxMultiplier: Number(settings?.global_max_multiplier ?? base.globalMaxMultiplier),
      globalMaxMarkupPct: Number(settings?.global_max_markup_pct ?? base.globalMaxMarkupPct),
      categoryRules,
      matrixByCategory,
    };
  }, [sQ.data, rQ.data, mQ.data]);

  return {
    config,
    isLoading: sQ.isLoading || rQ.isLoading || mQ.isLoading,
    hasShopConfig: !!sQ.data || !!rQ.data?.length || !!mQ.data?.length,
  };
}

// ── Mutations ────────────────────────────────────────────────────────────

export function useUpsertPricingSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Partial<ShopPricingSettingsRow> & { shop_id: string }) => {
      const { data, error } = await (supabase as any)
        .from('shop_pricing_settings')
        .upsert(input, { onConflict: 'shop_id' })
        .select()
        .single();
      if (error) throw error;
      return data as ShopPricingSettingsRow;
    },
    onSuccess: (row) => qc.invalidateQueries({ queryKey: settingsKey(row.shop_id) }),
  });
}

export function useUpsertCategoryRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Partial<ShopPricingCategoryRuleRow> & { shop_id: string; category: string }) => {
      const { data, error } = await (supabase as any)
        .from('shop_pricing_category_rules')
        .upsert(input, { onConflict: 'shop_id,category' })
        .select()
        .single();
      if (error) throw error;
      return data as ShopPricingCategoryRuleRow;
    },
    onSuccess: (row) => qc.invalidateQueries({ queryKey: rulesKey(row.shop_id) }),
  });
}

export function useDeleteCategoryRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; shopId: string }) => {
      const { error } = await (supabase as any)
        .from('shop_pricing_category_rules')
        .delete()
        .eq('id', args.id);
      if (error) throw error;
      return args;
    },
    onSuccess: (args) => qc.invalidateQueries({ queryKey: rulesKey(args.shopId) }),
  });
}

export function useUpsertMatrixRow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Partial<ShopPricingMatrixRowRow> & { shop_id: string }) => {
      const { data, error } = await (supabase as any)
        .from('shop_pricing_matrix_rows')
        .upsert(input)
        .select()
        .single();
      if (error) throw error;
      return data as ShopPricingMatrixRowRow;
    },
    onSuccess: (row) => qc.invalidateQueries({ queryKey: matrixKey(row.shop_id) }),
  });
}

export function useDeleteMatrixRow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; shopId: string }) => {
      const { error } = await (supabase as any)
        .from('shop_pricing_matrix_rows')
        .delete()
        .eq('id', args.id);
      if (error) throw error;
      return args;
    },
    onSuccess: (args) => qc.invalidateQueries({ queryKey: matrixKey(args.shopId) }),
  });
}
